import { ClientAiProfileEntity } from '@/entities/client-ai-profile.entity';
import { ClientAiUsageSummaryEntity } from '@/entities/client-ai-usage-summary.entity';
import { ClientAnswerKindSummaryEntity } from '@/entities/client-answer-kind-summary.entity';
import { ClientFeatureControlsEntity } from '@/entities/client-feature-controls.entity';
import { ClientMarketplaceAvailabilityEntity } from '@/entities/client-marketplace-availability.entity';
import { ClientMarketplaceSettingsConstraintsEntity } from '@/entities/client-marketplace-settings-constraints.entity';
import { ClientMarketplaceSettingsEntity } from '@/entities/client-marketplace-settings.entity';
import { ClientOnboardingEntity } from '@/entities/client-onboarding.entity';
import { ClientQuestionnaireEntity } from '@/entities/client-questionnaire.entity';
import { ClientSettingsEntity } from '@/entities/client-settings.entity';
import { createDefaultClientProfile, normalizeClientProfileEntity } from '@/shared/client-profile';
import { createDefaultClientAiQuotaSummary, normalizeClientAiQuotaSummaryEntity } from '@/shared/client-ai-quota';
import { createDefaultClientBillingSummary, normalizeClientBillingSummaryEntity } from '@/shared/client-billing';
import {
    clientMarketplaceSettingsToAvailability,
    createDefaultClientMarketplaceSettings,
    createDefaultClientMarketplaceSettingsConstraints,
    marketplaceAvailabilityToClientMarketplaceSettings,
    normalizeClientMarketplaceSettingsConstraintsEntity,
    normalizeClientMarketplaceSettingsEntity,
} from '@/shared/client-marketplace-settings';
import {
    CLIENT_QUESTIONNAIRE_ANSWER_KEYS,
    ClientQuestionnaireAnswerId,
    applyClientQuestionnaireHiddenDefaultAnswers,
    buildEmptyClientQuestionnaireAnswers,
    normalizeClientQuestionnaireAnswers,
    removeClientQuestionnaireHiddenDefaultAnswers,
} from '@/shared/client-questionnaire-definition';
import { isOnboardingAgreementLocale } from '@/shared/onboarding-agreement';
import { Prisma } from '@prisma/client';

type JsonObject = Prisma.InputJsonObject;
type LegacyAnswersSource = Record<string, unknown>;
type ClientSettingsEditableFields = Pick<
    ClientSettingsEntity,
    'clientProfile' | 'aiProfile' | 'featureControls' | 'marketplaceSettings'
>;

const LEGACY_QUESTIONNAIRE_ANSWER_KEYS = [
    'market_role',
    'planning_horizon',
    'price_positioning',
    'priority_now',
    'can_improve_products',
    'comm_channel',
    'return_policy',
    'change_policy',
    'defect_feedback_style',
    'usage_recommendations',
    'attitude_negative_reviews',
    'quick_response',
    'preferred_tone',
    'advanced_analytics',
    'growth_invest',
    'attitude_criticism',
] as const;

const toObjectOrNull = (value: unknown): Record<string, unknown> | null => {
    if (!value || typeof value !== 'object' || Array.isArray(value)) {
        return null;
    }

    return value as Record<string, unknown>;
};

const toStringOrEmpty = (value: unknown): string => (typeof value === 'string' ? value : '');

const stableJsonClone = (value: unknown): unknown => {
    if (Array.isArray(value)) {
        return value.map(stableJsonClone);
    }

    if (!value || typeof value !== 'object') {
        return value;
    }

    const source = value as Record<string, unknown>;
    return Object.keys(source)
        .sort()
        .reduce<Record<string, unknown>>((result, key) => {
            result[key] = stableJsonClone(source[key]);
            return result;
        }, {});
};

const fnv1a64 = (value: string): string => {
    let hash = 0xcbf29ce484222325n;
    const prime = 0x100000001b3n;
    const mask = 0xffffffffffffffffn;

    for (let index = 0; index < value.length; index += 1) {
        hash ^= BigInt(value.charCodeAt(index));
        hash = (hash * prime) & mask;
    }

    return hash.toString(16).padStart(16, '0');
};

export const buildClientSettingsEditableVersion = (settings: ClientSettingsEditableFields): string => {
    const snapshot = {
        clientProfile: {
            email: normalizeClientProfileEntity(settings.clientProfile).email.trim(),
        },
        aiProfile: normalizeClientAiProfileEntity(settings.aiProfile),
        featureControls: normalizeClientFeatureControlsEntity(settings.featureControls),
        marketplaceSettings: normalizeClientMarketplaceSettingsEntity(settings.marketplaceSettings),
    };

    return `settings-v1:${fnv1a64(JSON.stringify(stableJsonClone(snapshot)))}`;
};

export const withClientSettingsVersion = <TSettings extends ClientSettingsEditableFields>(
    settings: TSettings,
): TSettings & { version: string } => ({
    ...settings,
    version: buildClientSettingsEditableVersion(settings),
});

const resolveLegacyDirectMapping = (rawValue: unknown, optionMap: Record<string, string>): string =>
    optionMap[toStringOrEmpty(rawValue)] ?? '';

const resolveLegacyReturnsAnswer = (answers: LegacyAnswersSource): string => {
    const normalizedReturn = resolveLegacyDirectMapping(answers.return_policy, {
        both: 'Да, и возврат, и замену',
        exchange_only: 'Да, только замену товара',
        return_only: 'Да, только возврат денег',
        marketplace_managed: 'Нет, этим занимается площадка',
    });
    const normalizedChange = resolveLegacyDirectMapping(answers.change_policy, {
        both: 'Да, и возврат, и замену',
        exchange_only: 'Да, только замену товара',
        return_only: 'Да, только возврат денег',
        marketplace_managed: 'Нет, этим занимается площадка',
    });

    if (normalizedReturn && normalizedChange && normalizedReturn !== normalizedChange) {
        return '';
    }

    return normalizedReturn || normalizedChange;
};

export const buildEmptyClientAiProfileAnswers = (): Record<ClientQuestionnaireAnswerId, string> => {
    return buildEmptyClientQuestionnaireAnswers();
};

const hasNewQuestionnaireShape = (answers: Record<string, unknown> | null): boolean => {
    if (!answers) {
        return false;
    }

    return CLIENT_QUESTIONNAIRE_ANSWER_KEYS.some((answerKey) => answerKey in answers);
};

export const mapLegacyQuestionnaireAnswersToClientAiProfileAnswers = (
    answersValue: unknown,
): Record<ClientQuestionnaireAnswerId, string> => {
    const normalizedAnswers = buildEmptyClientAiProfileAnswers();
    const answers = toObjectOrNull(answersValue);

    if (!answers) {
        return normalizedAnswers;
    }

    normalizedAnswers.position = resolveLegacyDirectMapping(answers.market_role, {
        manufacturer: 'Производитель',
        official_distributor: 'Официальный дистрибьютор',
        reseller_wholesale: 'Перепродавец (опт + розница)',
        reseller_repack: 'Реселлер (переупаковка чужого товара)',
    });
    normalizedAnswers.horizon = resolveLegacyDirectMapping(answers.planning_horizon, {
        liquidation: 'До полугода, распродажа остатков',
        medium_term: 'От полугода до года',
        long_term: 'От года до двух лет',
        flagship: 'Больше двух лет, долгий флагман',
    });
    normalizedAnswers.price = resolveLegacyDirectMapping(answers.price_positioning, {
        budget: 'Низкий, бюджетный сегмент',
        mid_market: 'Средний, массовый сегмент',
        premium: 'Высокий, премиальный сегмент',
        luxury: 'Разный, несколько ценовых линеек',
    });
    normalizedAnswers.priority = resolveLegacyDirectMapping(answers.priority_now, {
        growth: 'Рост рейтинга и отзывов',
        balance: 'Баланс рейтинга и прибыли',
        revenue: 'Максимальная прибыль',
        expansion: 'Выход на новые площадки',
    });
    normalizedAnswers.control = resolveLegacyDirectMapping(answers.can_improve_products, {
        yes: 'Полностью контролирую производство',
        partially: 'Могу просить завод доработать',
        no: 'Совсем не влияю на качество',
    });
    normalizedAnswers.channels = resolveLegacyDirectMapping(answers.comm_channel, {
        direct_messages: 'Да, личные сообщения на площадке',
        public_comments_only: 'Только ответы в отзывах',
        no_direct_contacts: 'Нет никакого прямого контакта',
    });
    normalizedAnswers.returns = resolveLegacyReturnsAnswer(answers);
    normalizedAnswers.sla = resolveLegacyDirectMapping(answers.quick_response, {
        urgent: 'В течение двух часов',
        standard: 'В течение одного дня',
        normal: 'В течение трёх дней',
        rare: 'Редко, позже трёх дней',
    });
    normalizedAnswers.acknowledgment = resolveLegacyDirectMapping(answers.defect_feedback_style, {
        honest: 'Всегда пишу, что виноват товар, не клиент',
        balanced: 'Чаще пишу, что виноват товар, редко клиент',
        defensive: 'Часто объясняю, что клиент сам виноват',
    });
    normalizedAnswers.advice = resolveLegacyDirectMapping(answers.usage_recommendations, {
        always: 'Да, всегда и подробно',
        sometimes: 'Да, только когда есть ошибка клиента',
        proactive_guides: 'Да, через заранее подготовленные гайды',
        no: 'Нет, это не моя зона ответственности',
    });
    normalizedAnswers.attitude = resolveLegacyDirectMapping(answers.attitude_negative_reviews, {
        threat: 'Как к угрозе рейтингу',
        information: 'Как к полезной информации',
        opportunity: 'Как к возможности стать лучше',
        normal: 'Как к обычной части работы',
    });
    normalizedAnswers.tone = resolveLegacyDirectMapping(answers.preferred_tone, {
        formal: 'Официальный, как письмо от компании',
        friendly: 'Дружелюбный, как живой диалог',
        business: 'Деловой, кратко и по фактам',
    });
    normalizedAnswers.taxonomy = resolveLegacyDirectMapping(answers.advanced_analytics, {
        regular: 'Регулярно считаю и ищу повторяющиеся темы',
        sometimes: 'Замечаю повторы, но без подсчётов',
        automated: 'Регулярно считаю и ищу повторяющиеся темы',
        none: 'Почти не анализирую отзывы',
    });
    normalizedAnswers.roi_improvements = resolveLegacyDirectMapping(answers.growth_invest, {
        constant: 'Да, регулярно вносим улучшения',
        roi_based: 'Да, если очевидна выгода',
        partial: 'Иногда, если есть свободные ресурсы',
        none: 'Нет, почти ничего не меняем',
    });

    return applyClientQuestionnaireHiddenDefaultAnswers(normalizedAnswers);
};

export const createDefaultClientAiProfile = (): ClientAiProfileEntity => ({
    storeName: '',
    answers: buildEmptyClientAiProfileAnswers(),
});

export const createDefaultClientFeatureControls = (): ClientFeatureControlsEntity => ({
    allowResponseSending: true,
    allowAiAnswerGeneration: true,
    allowAiAnalysis: true,
    autoReviewAiAnalysisEnabled: false,
    autoReviewProcessingEnabled: false,
});

export const createDefaultClientMarketplaceAvailability = (): ClientMarketplaceAvailabilityEntity => ({
    wb: false,
    ozon: false,
    yandexMarket: false,
});

export const createDefaultClientMarketplaceSettingsEntity = (): ClientMarketplaceSettingsEntity =>
    createDefaultClientMarketplaceSettings();

export const createDefaultClientMarketplaceSettingsConstraintsEntity = (): ClientMarketplaceSettingsConstraintsEntity =>
    createDefaultClientMarketplaceSettingsConstraints();

export const createDefaultClientAiUsageSummary = (): ClientAiUsageSummaryEntity => ({
    generatedAt: null,
    totals: {
        templateGeneration: 0,
        reviewAnalysis: 0,
        aiAnswerGeneration: 0,
    },
    effectiveAmount: 0,
});

export const createDefaultClientAnswerKindSummary = (): ClientAnswerKindSummaryEntity => ({
    generatedAt: null,
    totals: {
        human: 0,
        template: 0,
        aiUnchanged: 0,
        aiChanged: 0,
    },
    aiUnchangedPercent: null,
    daily: [],
});

export const createDefaultClientOnboarding = (): ClientOnboardingEntity => ({
    agreementAccepted: false,
    agreementAcceptedAt: null,
    agreementAcceptedOfferVersion: null,
    agreementAcceptedLocale: null,
});

export const createDefaultClientSettings = (): ClientSettingsEntity =>
    withClientSettingsVersion({
        clientProfile: createDefaultClientProfile(),
        aiProfile: createDefaultClientAiProfile(),
        aiQuota: createDefaultClientAiQuotaSummary(),
        aiUsage: createDefaultClientAiUsageSummary(),
        answerKinds: createDefaultClientAnswerKindSummary(),
        billing: createDefaultClientBillingSummary(),
        featureControls: createDefaultClientFeatureControls(),
        marketplaceSettings: createDefaultClientMarketplaceSettingsEntity(),
        marketplaceSettingsConstraints: createDefaultClientMarketplaceSettingsConstraintsEntity(),
        onboarding: createDefaultClientOnboarding(),
    });

export const normalizeClientAiProfileEntity = (value: unknown): ClientAiProfileEntity => {
    const defaults = createDefaultClientAiProfile();
    const source = toObjectOrNull(value);
    if (!source) {
        return defaults;
    }

    let normalizedAnswers = buildEmptyClientAiProfileAnswers();
    const sourceAnswers = toObjectOrNull(source.answers);

    if (hasNewQuestionnaireShape(sourceAnswers)) {
        normalizedAnswers = normalizeClientQuestionnaireAnswers(sourceAnswers);
    } else if (
        sourceAnswers &&
        LEGACY_QUESTIONNAIRE_ANSWER_KEYS.some((legacyAnswerKey) =>
            Object.prototype.hasOwnProperty.call(sourceAnswers, legacyAnswerKey),
        )
    ) {
        normalizedAnswers = mapLegacyQuestionnaireAnswersToClientAiProfileAnswers(sourceAnswers);
    }

    return {
        storeName: typeof source.storeName === 'string' ? source.storeName : '',
        ...(typeof source.companyInfoLink === 'string' ? { companyInfoLink: source.companyInfoLink } : {}),
        answers: applyClientQuestionnaireHiddenDefaultAnswers(normalizedAnswers),
        ...(typeof source.comments === 'string' ? { comments: source.comments } : {}),
    };
};

export const normalizeClientFeatureControlsEntity = (value: unknown): ClientFeatureControlsEntity => {
    const defaults = createDefaultClientFeatureControls();
    const source = toObjectOrNull(value);
    if (!source) {
        return defaults;
    }

    return {
        allowResponseSending:
            typeof source.allowResponseSending === 'boolean'
                ? source.allowResponseSending
                : defaults.allowResponseSending,
        allowAiAnswerGeneration:
            typeof source.allowAiAnswerGeneration === 'boolean'
                ? source.allowAiAnswerGeneration
                : typeof source.allowAiResponses === 'boolean'
                  ? source.allowAiResponses
                  : defaults.allowAiAnswerGeneration,
        allowAiAnalysis:
            typeof source.allowAiAnalysis === 'boolean' ? source.allowAiAnalysis : defaults.allowAiAnalysis,
        autoReviewAiAnalysisEnabled:
            typeof source.autoReviewAiAnalysisEnabled === 'boolean'
                ? source.autoReviewAiAnalysisEnabled
                : typeof source.enableAutoReviewAiAnalysis === 'boolean'
                  ? source.enableAutoReviewAiAnalysis
                  : defaults.autoReviewAiAnalysisEnabled,
        autoReviewProcessingEnabled:
            typeof source.autoReviewProcessingEnabled === 'boolean'
                ? source.autoReviewProcessingEnabled
                : typeof source.enableAutoReviewProcessing === 'boolean'
                  ? source.enableAutoReviewProcessing
                  : defaults.autoReviewProcessingEnabled,
    };
};

export const normalizeClientMarketplaceAvailabilityEntity = (value: unknown): ClientMarketplaceAvailabilityEntity => {
    const defaults = createDefaultClientMarketplaceAvailability();
    const source = toObjectOrNull(value);
    if (!source) {
        return defaults;
    }

    return {
        wb: typeof source.wb === 'boolean' ? source.wb : defaults.wb,
        ozon: typeof source.ozon === 'boolean' ? source.ozon : defaults.ozon,
        yandexMarket: typeof source.yandexMarket === 'boolean' ? source.yandexMarket : defaults.yandexMarket,
    };
};

const normalizeNonNegativeInteger = (value: unknown): number =>
    typeof value === 'number' && Number.isInteger(value) && value >= 0 ? value : 0;

export const normalizeClientAiUsageSummaryEntity = (value: unknown): ClientAiUsageSummaryEntity => {
    const defaults = createDefaultClientAiUsageSummary();
    const source = toObjectOrNull(value);
    if (!source) {
        return defaults;
    }

    const sourceTotals = toObjectOrNull(source.totals);
    const totals = {
        templateGeneration: normalizeNonNegativeInteger(sourceTotals?.templateGeneration),
        reviewAnalysis: normalizeNonNegativeInteger(sourceTotals?.reviewAnalysis),
        aiAnswerGeneration: normalizeNonNegativeInteger(sourceTotals?.aiAnswerGeneration),
    };

    return {
        generatedAt: typeof source.generatedAt === 'string' ? source.generatedAt : null,
        totals,
        effectiveAmount:
            typeof source.effectiveAmount === 'number' && Number.isInteger(source.effectiveAmount)
                ? Math.max(source.effectiveAmount, 0)
                : Math.max(totals.templateGeneration, totals.reviewAnalysis, totals.aiAnswerGeneration),
    };
};

export const normalizeClientAnswerKindDailyPointEntity = (
    value: unknown,
): ClientAnswerKindSummaryEntity['daily'][number] => {
    const source = toObjectOrNull(value);
    const sourceTotals = toObjectOrNull(source?.totals);
    const totals = {
        human: normalizeNonNegativeInteger(sourceTotals?.human),
        template: normalizeNonNegativeInteger(sourceTotals?.template),
        aiUnchanged: normalizeNonNegativeInteger(sourceTotals?.aiUnchanged),
        aiChanged: normalizeNonNegativeInteger(sourceTotals?.aiChanged),
    };
    const aiTotal = totals.aiUnchanged + totals.aiChanged;

    return {
        day: typeof source?.day === 'string' ? source.day : '',
        totals,
        aiUnchangedPercent:
            typeof source?.aiUnchangedPercent === 'number' && Number.isFinite(source.aiUnchangedPercent)
                ? source.aiUnchangedPercent
                : aiTotal > 0
                  ? (totals.aiUnchanged / aiTotal) * 100
                  : null,
    };
};

export const normalizeClientAnswerKindSummaryEntity = (value: unknown): ClientAnswerKindSummaryEntity => {
    const defaults = createDefaultClientAnswerKindSummary();
    const source = toObjectOrNull(value);
    if (!source) {
        return defaults;
    }

    const sourceTotals = toObjectOrNull(source.totals);
    const totals = {
        human: normalizeNonNegativeInteger(sourceTotals?.human),
        template: normalizeNonNegativeInteger(sourceTotals?.template),
        aiUnchanged: normalizeNonNegativeInteger(sourceTotals?.aiUnchanged),
        aiChanged: normalizeNonNegativeInteger(sourceTotals?.aiChanged),
    };
    const aiTotal = totals.aiUnchanged + totals.aiChanged;

    return {
        generatedAt: typeof source.generatedAt === 'string' ? source.generatedAt : null,
        totals,
        aiUnchangedPercent:
            typeof source.aiUnchangedPercent === 'number' && Number.isFinite(source.aiUnchangedPercent)
                ? source.aiUnchangedPercent
                : aiTotal > 0
                  ? (totals.aiUnchanged / aiTotal) * 100
                  : null,
        daily: Array.isArray(source.daily)
            ? source.daily.map((point) => normalizeClientAnswerKindDailyPointEntity(point))
            : defaults.daily,
    };
};

const normalizeNullableIsoDateString = (value: unknown): string | null => {
    if (value instanceof Date) {
        return value.toISOString();
    }

    if (typeof value !== 'string' || value.trim() === '') {
        return null;
    }

    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : value;
};

export const normalizeClientOnboardingEntity = (value: unknown): ClientOnboardingEntity => {
    const defaults = createDefaultClientOnboarding();
    const source = toObjectOrNull(value);
    if (!source) {
        return defaults;
    }

    const agreementAcceptedAt = normalizeNullableIsoDateString(source.agreementAcceptedAt);

    return {
        agreementAccepted:
            typeof source.agreementAccepted === 'boolean' ? source.agreementAccepted : agreementAcceptedAt !== null,
        agreementAcceptedAt,
        agreementAcceptedOfferVersion:
            typeof source.agreementAcceptedOfferVersion === 'string' && source.agreementAcceptedOfferVersion.trim()
                ? source.agreementAcceptedOfferVersion
                : null,
        agreementAcceptedLocale: isOnboardingAgreementLocale(source.agreementAcceptedLocale)
            ? source.agreementAcceptedLocale
            : null,
    };
};

export const mapLegacyQuestionnaireToClientSettings = (value: unknown): ClientSettingsEntity => {
    const defaults = createDefaultClientSettings();
    const source = toObjectOrNull(value);
    if (!source) {
        return defaults;
    }

    const sourceSettings = toObjectOrNull(source.settings);

    return withClientSettingsVersion({
        clientProfile: defaults.clientProfile,
        aiProfile: {
            storeName: typeof source.storeName === 'string' ? source.storeName : '',
            ...(typeof source.companyInfoLink === 'string' ? { companyInfoLink: source.companyInfoLink } : {}),
            answers: mapLegacyQuestionnaireAnswersToClientAiProfileAnswers(source.answers),
            ...(typeof source.comments === 'string' ? { comments: source.comments } : {}),
        },
        aiQuota: defaults.aiQuota,
        aiUsage: defaults.aiUsage,
        answerKinds: defaults.answerKinds,
        billing: defaults.billing,
        featureControls: normalizeClientFeatureControlsEntity(toObjectOrNull(sourceSettings?.general)),
        marketplaceSettings: marketplaceAvailabilityToClientMarketplaceSettings(
            normalizeClientMarketplaceAvailabilityEntity(toObjectOrNull(sourceSettings?.channels)),
        ),
        marketplaceSettingsConstraints: defaults.marketplaceSettingsConstraints,
        onboarding: defaults.onboarding,
    });
};

export const normalizeClientSettingsEntity = (value: unknown): ClientSettingsEntity => {
    const defaults = createDefaultClientSettings();
    const source = toObjectOrNull(value);
    if (!source) {
        return defaults;
    }

    const hasNewSettingsShape =
        Object.prototype.hasOwnProperty.call(source, 'aiProfile') ||
        Object.prototype.hasOwnProperty.call(source, 'aiQuota') ||
        Object.prototype.hasOwnProperty.call(source, 'aiUsage') ||
        Object.prototype.hasOwnProperty.call(source, 'answerKinds') ||
        Object.prototype.hasOwnProperty.call(source, 'billing') ||
        Object.prototype.hasOwnProperty.call(source, 'clientProfile') ||
        Object.prototype.hasOwnProperty.call(source, 'featureControls') ||
        Object.prototype.hasOwnProperty.call(source, 'marketplaceSettings') ||
        Object.prototype.hasOwnProperty.call(source, 'onboarding');

    if (!hasNewSettingsShape) {
        return mapLegacyQuestionnaireToClientSettings(value);
    }

    return withClientSettingsVersion({
        clientProfile: normalizeClientProfileEntity(source.clientProfile),
        aiProfile: normalizeClientAiProfileEntity(source.aiProfile),
        aiQuota: normalizeClientAiQuotaSummaryEntity(source.aiQuota),
        aiUsage: normalizeClientAiUsageSummaryEntity(source.aiUsage),
        answerKinds: normalizeClientAnswerKindSummaryEntity(source.answerKinds),
        billing: normalizeClientBillingSummaryEntity(source.billing),
        featureControls: normalizeClientFeatureControlsEntity(source.featureControls),
        marketplaceSettings: normalizeClientMarketplaceSettingsEntity(source.marketplaceSettings),
        marketplaceSettingsConstraints: normalizeClientMarketplaceSettingsConstraintsEntity(
            source.marketplaceSettingsConstraints,
        ),
        onboarding: normalizeClientOnboardingEntity(source.onboarding),
    });
};

export const buildLegacyQuestionnaireEntityFromClientSettings = (
    settings: ClientSettingsEntity,
): ClientQuestionnaireEntity => ({
    storeName: settings.aiProfile.storeName,
    ...(settings.aiProfile.companyInfoLink ? { companyInfoLink: settings.aiProfile.companyInfoLink } : {}),
    answers: settings.aiProfile.answers,
    ...(settings.aiProfile.comments ? { comments: settings.aiProfile.comments } : {}),
    settings: {
        general: {
            allowResponseSending: settings.featureControls.allowResponseSending,
            allowAiResponses: settings.featureControls.allowAiAnswerGeneration,
            allowAiAnalysis: settings.featureControls.allowAiAnalysis,
            enableAutoReviewAiAnalysis: settings.featureControls.autoReviewAiAnalysisEnabled,
            enableAutoReviewProcessing: settings.featureControls.autoReviewProcessingEnabled,
        },
        channels: clientMarketplaceSettingsToAvailability(settings.marketplaceSettings),
    },
});

export const serializeClientAiProfile = (value: ClientAiProfileEntity): JsonObject => {
    return {
        storeName: value.storeName,
        answers: removeClientQuestionnaireHiddenDefaultAnswers(value.answers) as Prisma.InputJsonObject,
        ...(value.companyInfoLink ? { companyInfoLink: value.companyInfoLink } : {}),
        ...(value.comments ? { comments: value.comments } : {}),
    } as JsonObject;
};

export const serializeClientFeatureControls = (value: ClientFeatureControlsEntity): JsonObject => ({
    allowResponseSending: value.allowResponseSending,
    allowAiAnswerGeneration: value.allowAiAnswerGeneration,
    allowAiAnalysis: value.allowAiAnalysis,
    autoReviewAiAnalysisEnabled: value.autoReviewAiAnalysisEnabled,
    autoReviewProcessingEnabled: value.autoReviewProcessingEnabled,
});

export const serializeClientMarketplaceAvailability = (value: ClientMarketplaceAvailabilityEntity): JsonObject => ({
    wb: value.wb,
    ozon: value.ozon,
    yandexMarket: value.yandexMarket,
});
